
import { createVNode, defineComponent } from 'vue';
import { mapState } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import {
  CheckOutlined,
  EditOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue';
import numeral from 'numeral';
import cloneDeep from 'lodash.clonedeep';
import SearchIcon from '@/components/icons/SearchIcon.vue';
import UploadIcon from '@/components/icons/UploadIcon.vue';
import UploadModal from './UploadModal.vue';
import UploadResultModal from './UploadResultModal.vue';
import DeleteIcon from '@/components/icons/DeleteIcon.vue';
import QuantityInput from '@/components/quantity-input/QuantityInput.vue';
import {
  getShoppingcartPage,
  deleteShoppingcart,
  modifyShoppingcart,
  submitOrder,
  deleteShoppingcartItemById
} from '@/api/apis';

interface PartCartProps {
  dataSource: any[];
  originDataSource: any[];
  selectedRowKeys: any[];
  [key: string]: any;
}

export default defineComponent({
  name: 'PartCart',
  components: {
    SearchIcon,
    UploadIcon,
    UploadModal,
    UploadResultModal,
    QuantityInput,
    DeleteIcon,
    CheckOutlined,
    EditOutlined
  },
  props: {
    consigneeDropdownList: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data(): PartCartProps {
    return {
      numeral,
      queryParam: {
        partName: '',
        modelNum: ''
      },
      selectedRowKeys: [],
      loading: false,
      columns: [
        {
          title: this.$t('shopping_cart.vehicle_name'),
          dataIndex: 'vehicleName',
          width: 100,
          slots: { title: 'pvehicle_name-title', customRender: 'vehicleName' }
        },
        {
          title: this.$t('shopping_cart.sc_part_type'),
          dataIndex: 'groupName',
          width: 100,
          slots: { title: 'part_type-title', customRender: 'groupName' }
        },
        {
          title: this.$t('shopping_cart.sc_name'),
          dataIndex: 'name',
          width: 160,
          slots: { title: 'name-title', customRender: 'name' }
        },
        {
          title: this.$t('shopping_cart.sc_part_no'),
          dataIndex: 'modelNum',
          width: 180,
          slots: { customRender: 'modelNum', title: 'part_no-title' }
        },
        {
          title: this.$t('shopping_cart.sc_part_code'),
          dataIndex: 'partCode',
          width: 100,
          slots: { title: 'part_code-title', customRender: 'part-code' }
        },
        {
          title: this.$t('shopping_cart.sc_unit'),
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'quantity',
          width: 150,
          slots: { customRender: 'quantity', title: 'quantity-title' }
        },
        // {
        //   dataIndex: 'unitPrice',
        //   width: 100,
        //   slots: { customRender: 'unitPrice', title: 'unit_price-title' }
        // },
        // {
        //   dataIndex: 'totalPrice',
        //   width: 100,
        //   slots: { customRender: 'totalPrice', title: 'total_price-title' }
        // },
        {
          dataIndex: 'consignee',
          width: 140,
          slots: { title: 'consignee-title', customRender: 'consignee' }
        },
        {
          dataIndex: 'remark',
          width: 120,
          slots: { title: 'remark-title', customRender: 'remark' }
        },
        {
          width: 100,
          slots: { title: 'action-title', customRender: 'action' }
        }
      ],
      isSubmitting: false,
      colTitle: [
        'part_type',
        'name',
        'part_no',
        'part_code',
        'unit',
        'quantity',
        'unit_price',
        'total_price',
        'action',
        'consignee',
        'remark'
      ],
      dataSource: [],
      showUploadModal: false,
      showResultModal: false,
      originDataSource: [],

      isDeleting: false
    };
  },
  created() {
    this.loadData();
  },
  computed: {
    totalSummary() {
      const items = this.dataSource.filter(s =>
        this.selectedRowKeys.includes(s.id)
      );

      let total = numeral(0);

      items.forEach(s => {
        total = total.add(s.totalPrice || 0);
      });

      const value = total.value();

      return value ? total.format('$0,0.00') : '';
    },
    ...mapState(['userInfo'])
  },
  methods: {
    handleClear() {
      Modal.confirm({
        // title: 'Are you sure delete this task?',
        icon: createVNode(ExclamationCircleOutlined),
        content: this.$t('shopping_cart.delete_confirm'),
        okText: this.$t('shopping_cart.confirm'),
        cancelText: this.$t('shopping_cart.cancel'),
        onOk: async () => {
          const res = await deleteShoppingcartItemById({
            ids: this.selectedRowKeys.join(',')
          });

          if (res.success) {
            this.selectedRowKeys = [];
            this.loadData();
          }
        }
      });
    },
    handleSearch() {
      this.dataSource = this.originDataSource.filter(s => {
        const modelBoolean =
          (this.queryParam.modelNum &&
            s.modelNum
              ?.toUpperCase()
              .indexOf(this.queryParam.modelNum.toUpperCase()) > -1) ||
          !this.queryParam.modelNum;
        const nameBoolean =
          (this.queryParam.partName &&
            this.$t(s.name || '')
              .toUpperCase()
              .indexOf(this.queryParam.partName.toUpperCase()) > -1) ||
          !this.queryParam.partName;

        return modelBoolean && nameBoolean;
      });
    },
    handleModelChange() {
      if (!this.queryParam.modelNum) {
        this.handleSearch();
      }
    },
    handleNameChange() {
      if (!this.queryParam.partName) {
        this.handleSearch();
      }
    },
    async handleDelete(record) {
      const res = await deleteShoppingcart({
        id: record.id
      });

      if (res.success) {
        this.dataSource = this.dataSource.filter(
          (s: any) => s.id !== record.id
        );

        this.originDataSource = cloneDeep(this.dataSource);
      }
    },
    gotoDiagramDetail(record) {
      if (record.groupId) {
        this.$router.push(`/part-detail/${record.groupId}`);
      } else if (record.vehicleId) {
        this.$router.push(`/spare-part/${record.vehicleId}`);
      }
    },
    async loadData() {
      this.loading = true;
      const res = await getShoppingcartPage({
        modelNum: this.queryParam.modelNum,
        consignee: this.queryParam.consignee,
        shoppingType: '2,3',
        pageNo: 1,
        pageSize: 2147483647
      });

      if (res.success) {
        this.dataSource = res.result.records || [];

        this.originDataSource = cloneDeep(this.dataSource);
      }

      this.loading = false;
    },
    handleUpload() {
      this.showUploadModal = true;
    },
    handleAfterResultClose() {
      this.showResultModal = false;
    },
    handleAfterClose(isCancel) {
      if (!isCancel) {
        this.showResultModal = true;
      }
      this.showUploadModal = false;
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },
    getSearchBtn1Style() {
      const isActive = !!this.queryParam.modelNum;
      return {
        width: 'auto',
        padding: '0 15px',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    getSearchBtn2Style() {
      const isActive = !!this.queryParam.partName;
      return {
        width: 'auto',
        padding: '0 15px',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    async handleSubmitOrder() {
      const items = this.dataSource
        .filter(s => this.selectedRowKeys.includes(s.id))
        .map(s => {
          return {
            id: s.id,
            quantity: s.quantity,
            remark: s.remark
          };
        });

      this.isSubmitting = true;
      const res = await submitOrder(items);

      if (res.success) {
        this.selectedRowKeys = [];
        this.loadData();
      } else {
        message.error(this.$t(res.message));
      }

      this.isSubmitting = false;
    },
    async handleSave(record) {
      const res = await modifyShoppingcart({
        ...record
      });

      if (res.success) {
        record.editable = false;
        record.conEditable = false;
        const oIndex = this.originDataSource.findIndex(s => s.id === record.id);
        if (oIndex > -1) {
          this.originDataSource.splice(oIndex, 1, { ...record });
        }
      }
    },
    handleEdit(record) {
      record.editable = true;
    },
    handleConsigneeEdit(record) {
      record.conEditable = true;
    },
    async handleQuantityChange(record, quantity) {
      const res = await modifyShoppingcart({
        ...record,
        quantity
      });

      if (res.success) {
        record.totalPrice = record.unitPrice
          ? numeral(quantity)
              .multiply(record.unitPrice || 0)
              .value()
          : null;
      }

      record.quantity = quantity;
    }
  }
});
